import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import { useCopy } from '../../lib/copyToClipboard';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold,
  Italic,
  Strikethrough,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Undo,
  Redo,
  Maximize2,
  Minimize2,
  Sparkles,
  Loader2,
  X,
} from 'lucide-react';
import { Loader } from '../common/PageLoader';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /** Show expand control for a full-window writing surface. */
  expandable?: boolean;
  /** Label shown in the fullscreen header. */
  expandLabel?: string;
  /** Optional status line (e.g. autosave) under the editor or in the fullscreen header. */
  status?: ReactNode;
  /**
   * When provided, shows a "Summarize to bullets" toolbar action. Receives the
   * editor's current plain text and returns the generated bullet strings.
   */
  onRequestSummary?: (plainText: string) => Promise<string[]>;
}

const baseProse =
  'text-base text-cmp-ink focus:outline-none caret-current ' +
  '[&_p]:mb-2 [&_strong]:font-semibold [&_em]:italic ' +
  '[&_a]:text-cmp-bronze-2 [&_a]:underline [&_a]:underline-offset-2 ' +
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 ' +
  '[&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mb-2 ' +
  '[&_blockquote]:border-l-2 [&_blockquote]:border-cmp-line-2 [&_blockquote]:pl-3 [&_blockquote]:text-cmp-ink';

// Explicit min-heights — flex-1 alone collapses ProseMirror to 0px inside portals.
const contentClassDefault = `min-h-[10rem] max-h-[24rem] overflow-auto px-3 py-2 ${baseProse}`;
const contentClassExpanded =
  `min-h-[calc(100vh-9rem)] w-full max-w-3xl mx-auto px-6 py-8 md:px-10 ${baseProse} ` +
  '[&_p]:mb-3 [&_h2]:text-2xl [&_h2]:mb-3 [&_h3]:text-xl';

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  expandable = false,
  expandLabel = 'Writing',
  status,
  onRequestSummary,
}: RichTextEditorProps) {
  const [expanded, setExpanded] = useState(false);

  // Recreate the editor when entering/leaving fullscreen so ProseMirror
  // re-attaches to the new DOM (portal mount) with the current content.
  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({ link: false }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { rel: 'noreferrer', target: '_blank' },
        }),
        Placeholder.configure({ placeholder: placeholder || 'Write…' }),
      ],
      content: value || '',
      onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
      editorProps: {
        attributes: {
          class: expanded ? contentClassExpanded : contentClassDefault,
        },
      },
    },
    [expanded]
  );

  // Sync external value changes while mounted (cancel/reseed), without loops.
  useEffect(() => {
    if (!editor) return;
    const next = value || '';
    if (next !== editor.getHTML()) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    // Focus the writing surface after mount.
    const focusTimer = window.setTimeout(() => {
      editor?.commands.focus('end');
    }, 50);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(focusTimer);
    };
  }, [expanded, editor]);

  if (!editor) {
    return (
      <div className="rounded border border-cmp-line-2 bg-form-dark min-h-[10rem] flex items-center justify-center text-xs text-cmp-ink-3">
        <Loader label="Loading editor" />
      </div>
    );
  }

  const expandBtn = expandable ? (
    <button
      type="button"
      title={expanded ? 'Exit full screen (Esc)' : 'Expand to full screen'}
      onClick={() => setExpanded((v) => !v)}
      className="p-1.5 rounded hover:bg-cmp-bronze-soft text-cmp-ink"
    >
      {expanded ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
    </button>
  ) : null;

  const inline = (
    <div className="rounded border border-cmp-line-2 bg-form-dark">
      <Toolbar editor={editor} trailing={expandBtn} onRequestSummary={onRequestSummary} />
      <EditorContent editor={editor} />
      {status && (
        <div className="px-3 py-1.5 border-t border-cmp-line text-xs text-cmp-ink-2">{status}</div>
      )}
    </div>
  );

  if (!expanded) return inline;

  // Portal a true viewport overlay (escapes parent overflow:hidden). Solid
  // background + explicit content height so the writing area can't collapse.
  return (
    <>
      <div className="rounded border border-dashed border-cmp-line-2 bg-form-dark/40 px-3 py-6 text-sm text-cmp-ink-2">
        Editing body in full screen…
      </div>
      {createPortal(
        <div
          data-rich-text-expanded="true"
          className="fixed inset-0 z-[10050] flex h-[100dvh] w-screen flex-col bg-cmp-paper text-white"
        >
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-cmp-line bg-cmp-paper px-4 py-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-white">{expandLabel}</p>
              {status && <div className="mt-0.5 text-xs text-cmp-ink-2">{status}</div>}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {expandBtn}
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="rounded border border-cmp-line-2 px-3 py-1.5 text-sm text-cmp-ink hover:border-cmp-line-2"
              >
                Done
              </button>
            </div>
          </div>

          <div className="shrink-0 border-b border-cmp-line bg-cmp-surface">
            <Toolbar editor={editor} onRequestSummary={onRequestSummary} />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto bg-cmp-paper">
            <EditorContent editor={editor} />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

function Toolbar({
  editor,
  trailing,
  onRequestSummary,
}: {
  editor: Editor;
  trailing?: ReactNode;
  onRequestSummary?: (plainText: string) => Promise<string[]>;
}) {
  // Active: Ink's segment fill. (text-white is remapped to ink in tailwind.config.js,
  // so the old bg-gray-700 text-white hid the icon.)
  const btnClass = (active: boolean) =>
    `p-1.5 rounded hover:bg-cmp-bronze-soft ${active ? 'bg-[var(--ink-bg3)] text-cmp-ink' : 'text-cmp-ink'}`;

  const setLink = () => {
    const prev = (editor.getAttributes('link').href as string | undefined) || '';
    const url = window.prompt('Link URL', prev || 'https://');
    if (url === null) return;
    if (url.trim() === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 p-1">
      <button
        type="button"
        title="Bold"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={btnClass(editor.isActive('bold'))}
      >
        <Bold className="size-4" />
      </button>
      <button
        type="button"
        title="Italic"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={btnClass(editor.isActive('italic'))}
      >
        <Italic className="size-4" />
      </button>
      <button
        type="button"
        title="Strikethrough"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={btnClass(editor.isActive('strike'))}
      >
        <Strikethrough className="size-4" />
      </button>
      <span className="mx-1 h-5 w-px bg-gray-600" />
      <button
        type="button"
        title="Heading"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={btnClass(editor.isActive('heading', { level: 2 }))}
      >
        <Heading2 className="size-4" />
      </button>
      <button
        type="button"
        title="Subheading"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={btnClass(editor.isActive('heading', { level: 3 }))}
      >
        <Heading3 className="size-4" />
      </button>
      <button
        type="button"
        title="Bullet list"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={btnClass(editor.isActive('bulletList'))}
      >
        <List className="size-4" />
      </button>
      <button
        type="button"
        title="Numbered list"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={btnClass(editor.isActive('orderedList'))}
      >
        <ListOrdered className="size-4" />
      </button>
      <button
        type="button"
        title="Quote"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={btnClass(editor.isActive('blockquote'))}
      >
        <Quote className="size-4" />
      </button>
      <button
        type="button"
        title="Link"
        onClick={setLink}
        className={btnClass(editor.isActive('link'))}
      >
        <LinkIcon className="size-4" />
      </button>
      <span className="mx-1 h-5 w-px bg-gray-600" />
      <button
        type="button"
        title="Undo"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        className={`${btnClass(false)} disabled:opacity-40`}
      >
        <Undo className="size-4" />
      </button>
      <button
        type="button"
        title="Redo"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        className={`${btnClass(false)} disabled:opacity-40`}
      >
        <Redo className="size-4" />
      </button>
      {onRequestSummary && (
        <>
          <span className="mx-1 h-5 w-px bg-gray-600" />
          <SummarizeButton editor={editor} onRequestSummary={onRequestSummary} />
        </>
      )}
      {trailing && <span className="ml-auto flex items-center">{trailing}</span>}
    </div>
  );
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * "Summarize to bullets" toolbar action: atomizes the current body (server-side)
 * and shows key-takeaway bullets that can be inserted as a list or copied.
 */
export function SummarizeButton({
  editor,
  onRequestSummary,
}: {
  editor: Editor;
  onRequestSummary: (plainText: string) => Promise<string[]>;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bullets, setBullets] = useState<string[]>([]);
  const [error, setError] = useState('');
  const { copy: copyText, state: copyState } = useCopy();

  const run = async () => {
    setOpen(true);
    setLoading(true);
    setError('');
    setBullets([]);
    try {
      const result = await onRequestSummary(editor.getText());
      setBullets(result);
      if (result.length === 0) setError('Not enough factual content to summarize yet.');
    } catch (e) {
      setError((e as Error).message || 'Could not summarize the content.');
    } finally {
      setLoading(false);
    }
  };

  const insert = () => {
    if (!bullets.length) return;
    const html = `<ul>${bullets.map((b) => `<li>${escapeHtml(b)}</li>`).join('')}</ul>`;
    editor.chain().focus().insertContent(html).run();
    setOpen(false);
  };

  const copy = (e: { currentTarget: Element }) =>
    copyText('bullets', bullets.map((b) => `• ${b}`).join('\n'), e);

  return (
    <span className="relative">
      <button
        type="button"
        title="Summarize to bullets"
        onClick={run}
        className="flex items-center gap-1 rounded px-2 py-1 text-xs text-cmp-ink hover:bg-cmp-bronze-soft"
      >
        {loading ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
        <span className="hidden sm:inline">Summarize</span>
      </button>

      {open && (
        <div className="absolute right-0 z-[10060] mt-1 w-80 rounded border border-cmp-line-2 bg-cmp-surface p-3 text-sm shadow-lg">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-cmp-ink-2">
              Key takeaways
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-cmp-ink-3 hover:text-cmp-ink"
              aria-label="Close"
            >
              <X className="size-4" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 py-3 text-cmp-ink-2">
              <Loader2 className="size-4 animate-spin" /> Atomizing and summarizing…
            </div>
          ) : error ? (
            <p className="py-1 text-xs text-amber-300">{error}</p>
          ) : (
            <>
              <ul className="list-disc space-y-1 pl-5 text-cmp-ink">
                {bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={insert}
                  className="rounded bg-button-primary px-2.5 py-1 text-xs text-button-primary"
                >
                  Insert as list
                </button>
                <button
                  type="button"
                  onClick={copy}
                  className="rounded border border-cmp-line-2 px-2.5 py-1 text-xs text-cmp-ink hover:border-cmp-line-2"
                >
                  {copyState('bullets') === 'copied'
                    ? 'Copied'
                    : copyState('bullets') === 'failed'
                      ? 'Couldn’t copy'
                      : 'Copy'}
                </button>
                <button
                  type="button"
                  onClick={run}
                  className="rounded border border-cmp-line-2 px-2.5 py-1 text-xs text-cmp-ink hover:border-cmp-line-2"
                >
                  Regenerate
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </span>
  );
}
